import { createContext, useContext, useEffect } from 'react';

/**
 * 地区アクセス可否のガード (地区「強い停止」Phase 1)。
 *
 * 背景: キャッシュ済みの一般ユーザーは、地区が停止された後も古い (suspended=false) キャッシュで
 * アプリ画面が描画されてしまい、投稿等の購読だけが Firestore Rules で permission-denied になる
 * (UI は出るが投稿が「読込エラー: Missing or insufficient permissions」で止まる)。
 * これを「地区が非公開化された」とみなして DistrictProvider に通知し、DistrictGate (App.tsx) が
 * 「現在非公開中」画面へ切り替えるための仕組み。resolveDistrictByToken の suspended 返却に依存せず、
 * Rules の permission-denied シグナルだけで救済できるため、デプロイ状況によらず確実に動く。
 */
export interface DistrictAccessGuard {
  /** 投稿等の地区データ取得が permission-denied になり、地区が非公開化されている可能性が高い状態。 */
  accessDenied: boolean;
  /** 上記を通知する (DistrictProvider が状態を立て、DistrictGate が非公開画面へ切替)。 */
  reportAccessDenied: () => void;
}

export const DistrictAccessGuardContext = createContext<DistrictAccessGuard>({
  accessDenied: false,
  reportAccessDenied: () => {},
});

export function useDistrictAccessGuard(): DistrictAccessGuard {
  return useContext(DistrictAccessGuardContext);
}

/**
 * Firestore の permission-denied 系エラーか判定する (地区の非公開化 or claim 欠落で発生)。
 * FirebaseError の `code` ('permission-denied') と message ('Missing or insufficient permissions')
 * のどちらでも拾えるようにする。
 */
export function isPermissionDeniedError(error: unknown): boolean {
  if (!error) return false;
  const e = error as { code?: unknown; message?: unknown };
  const code = typeof e.code === 'string' ? e.code : '';
  const message = typeof e.message === 'string' ? e.message : '';
  return (
    code === 'permission-denied' ||
    code.endsWith('/permission-denied') ||
    /insufficient permissions|permission[-\s]?denied/i.test(message)
  );
}

/**
 * 地区データ取得エラーが permission-denied のとき、地区アクセス不可を DistrictProvider に通知する。
 * 通知後は DistrictGate が「現在非公開中」画面へ切り替える (キャッシュ済み一般ユーザーの停止後救済)。
 * permission-denied 以外のエラーや、Provider 外 (default context) では何もしない。
 */
export function useReportAccessDeniedOnError(error: unknown): void {
  const { reportAccessDenied } = useDistrictAccessGuard();
  const denied = isPermissionDeniedError(error);
  useEffect(() => {
    if (denied) reportAccessDenied();
  }, [denied, reportAccessDenied]);
}
